import { SiteUrls } from '@tableau/shared-urls';
import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';

import { ModalInstance, ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { ToasterService } from '~commonInfra/services/ToasterService';
import { assertUnreachable } from '~commonInfra/utils/TypescriptHelpers';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';

import { requestFailed } from '../../bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '../../codegen/Localize';
import { RemoveExternalContentDialogProps, removeExternalContentModalOptions } from '../components/RemoveExternalContentDialog';
import { RemoveExternalContentAction } from '../contentActions/RemoveExternalContentAction';
import { Databases } from '../resources/Databases';
import { Tables } from '../resources/Tables';

export enum RemoveButtonClicked {
  submitted = 'submitted',
  cancelled = 'cancelled',
}

function isRemoveButtonClicked(value: any): value is RemoveButtonClicked {
  return Object.keys(RemoveButtonClicked).includes(value);
}

export function* handleRemoveExternalContent(action: RemoveExternalContentAction): SagaIterator {
  try {
    const siteUrls: SiteUrls = yield select(siteUrlsSelector);
    const result: RemoveButtonClicked | any = yield call(openRemoveExternalContentDialog, action);
    const maybeNewUrl = window.location.origin + getContentListUrl(action, siteUrls);

    if (isRemoveButtonClicked(result)) {
      switch (result) {
        case RemoveButtonClicked.cancelled: break;
        case RemoveButtonClicked.submitted: {
          const toaster: ToasterService = ToasterService.instance;
          toaster.markToastsAsDoNotDismissOnLocationChange();

          if (window.location.href === maybeNewUrl) {
            window.location.reload();
          } else {
            window.location.href = maybeNewUrl;
          }
          break;
        }
        default: assertUnreachable(result);
      }
    }
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}
function getContentListUrl(action: RemoveExternalContentAction, siteUrls: SiteUrls) {
  switch(action.item.contentType) {
    case 'database': return siteUrls.catalogDatabasesUrl();
    case 'table': return siteUrls.catalogTablesUrl();
    default: assertUnreachable(action.item);
  }
}

export function openRemoveExternalContentDialog(action: RemoveExternalContentAction) {
  let modalInstance: ModalInstance<RemoveButtonClicked>;

  const removeExternalContent = () => {
    const item = action.item;
    switch(item.contentType) {
      case 'database': return Databases.deleteDatabase(item.vizportalId, item.name);
      case 'table': return Tables.deleteTable(item.vizportalId, item.name);
      default: assertUnreachable(item);
    }
  };

  const props: RemoveExternalContentDialogProps = {
    name: action.item.name,
    submit: () => removeExternalContent().then(() => modalInstance && modalInstance.close(RemoveButtonClicked.submitted)),
    cancel: () => modalInstance && modalInstance.close(RemoveButtonClicked.cancelled),
  };

  modalInstance = ReactModalService.open(removeExternalContentModalOptions(props));
  return modalInstance.result;
}
